/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import type { PropertyBusinessType } from '@/shared/utils/property-display';

const TYPE_STYLES: Record<Exclude<PropertyBusinessType, 'none'>, React.CSSProperties> = {
  sale: { background: 'color-mix(in srgb, var(--seed-base) 12%, var(--surface-primary))', color: 'var(--seed-base)' },
  rental: { background: 'color-mix(in srgb, var(--accent-petroleo) 14%, var(--surface-primary))', color: 'var(--accent-petroleo)' },
  both: { background: 'color-mix(in srgb, var(--accent-ambar) 16%, var(--surface-primary))', color: 'var(--accent-ambar)' },
  short_term: { background: 'color-mix(in srgb, var(--accent-salvia) 16%, var(--surface-primary))', color: 'var(--accent-salvia)' },
  auction: { background: 'color-mix(in srgb, var(--accent-cobre) 16%, var(--surface-primary))', color: 'var(--accent-cobre)' },
};

export interface BusinessTypeBadgeProps {
  type: PropertyBusinessType;
  /**
   * Legend under the price instead of a chip in a column of its own: the
   * background goes, the type's own color stays on the text, so Venda and
   * Locação remain tellable apart at a glance.
   */
  variant?: 'badge' | 'caption';
}

export const BusinessTypeBadge: React.FC<BusinessTypeBadgeProps> = ({ type, variant = 'badge' }) => {
  const { t } = useTranslation('features/properties');
  // Transaction types are optional; a listing with none gets no badge.
  if (type === 'none') return null;

  const styles: React.CSSProperties = variant === 'caption'
    ? {
        color: TYPE_STYLES[type].color,
        fontSize: 'var(--text-tag)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        lineHeight: 1.2,
      }
    : {
        ...TYPE_STYLES[type],
        padding: 'var(--space-1) var(--space-2)',
        fontSize: 'var(--text-meta)',
        borderRadius: 'var(--radius-md)',
      };

  return (
    <Text
      as="span"
      data-testid="business-type-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontWeight: 'var(--font-semibold)' as unknown as number,
        fontFamily: 'var(--font-family)',
        ...styles,
      }}
    >
      {t(`businessType.${type}`)}
    </Text>
  );
};
